"use client"

import { createContext, useContext, useState, useEffect } from "react"
import { getCurrentOrganization } from "../services/organization.service"
import { useAuth } from "./AuthContext"

const OrganizationContext = createContext()

export function OrganizationProvider({ children }) {
  const [currentOrganization, setCurrentOrganization] = useState(null)
  const [loading, setLoading] = useState(true)
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    // Check if we have a stored organization in localStorage
    const storedOrg = localStorage.getItem("currentOrganization")
    if (storedOrg) {
      try {
        setCurrentOrganization(JSON.parse(storedOrg))
      } catch (error) {
        console.error("Failed to parse stored organization:", error)
      }
    }

    // Fetch the current organization from your API
    const fetchCurrentOrganization = async () => {
      // Skip fetching if not authenticated
      if (!isAuthenticated) {
        setLoading(false)
        return
      }

      try {
        const response = await getCurrentOrganization()

        if (response.success) {
          setCurrentOrganization(response.data)
          // Store in localStorage for persistence
          localStorage.setItem("currentOrganization", JSON.stringify(response.data))
        } else {
          console.error("Failed to fetch organization:", response.error)
        }
      } catch (error) {
        console.error("Failed to fetch organization:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchCurrentOrganization()
  }, [isAuthenticated])

  const updateCurrentOrganization = (organization) => {
    setCurrentOrganization(organization)
    if (organization) {
      localStorage.setItem("currentOrganization", JSON.stringify(organization))
    } else {
      localStorage.removeItem("currentOrganization")
    }
  }

  const value = {
    currentOrganization,
    setCurrentOrganization: updateCurrentOrganization,
    loading,
  }

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>
}

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider")
  }
  return context
}

